import {
  Button,
  Flex,
  Spinner,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { DEFAULT_PARCEL_METHOD } from "@syla/shared/types/helpers/constants";
import {
  permissionFor,
  FeatureAccess,
} from "@syla/shared/types/helpers/permissions";
import { AccountPermission } from "@syla/shared/types/models/AccountAccessBase";
import { ParcelMethod } from "@syla/shared/types/models/AccountBase";
import React, { useMemo, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { FiEdit2 } from "react-icons/fi";
import { EditParcelMethodModal } from "../../components/molecules/forms/settingForms/EditParcelMethodModal";
import { AccountSubtypeTypeEditable } from "../../components/organisms/accounts/AccountSubtypeTypeEditable";
import { AccountTypeEditable } from "../../components/organisms/accounts/AccountTypeEditable";
import { hasAccountPermission } from "../../helper/permissions";
import { useGetAccountFinancialYearsData } from "../../store/actions/userFinancialYear";
import { useCurrentAccountStore } from "../../store/currentAccountStore";
import { useQueryGetAccount } from "../../store/useQueryGetAccount";
import { Account } from "../../types/Account";
import { ReadOnlySettingRow } from "./components/ReadOnlySettingRow";
import { SettingRowBase } from "./components/SettingRowBase";
import {
  SectionHeader,
  SectionContainer,
  SettingsStack,
} from "./components/SettingsComponents";

export function AccountTaxSettings() {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const queryGetAccount = useQueryGetAccount(accountId);

  const formProps = useForm<Partial<Account>>({
    defaultValues: queryGetAccount.data,
  });

  const { reset } = formProps;

  useEffect(() => {
    reset(queryGetAccount.data);
  }, [queryGetAccount.data, reset]);

  const isLoading = queryGetAccount.isLoading || !queryGetAccount.data;

  return (
    <SectionContainer>
      <SectionHeader
        title="Tax settings"
        description="Control how Syla performs tax calculations."
      />
      {isLoading ?
        <Flex justifyContent="center" my="40px">
          <Spinner />
        </Flex>
      : <SettingsStack>
          <FormProvider {...formProps}>
            <SettingRowBase
              label="Account type"
              description="The tax entity this account is for. This affects how your tax reports are prepared."
            >
              <AccountTypeEditable account={queryGetAccount.data} />
            </SettingRowBase>
            <SettingRowBase
              label="Activity type"
              description="The main purpose the crypto was acquired for. This affects how tax is calculated for crypto assets."
            >
              <AccountSubtypeTypeEditable account={queryGetAccount.data} />
            </SettingRowBase>
          </FormProvider>
          <ParcelMethodSettingRow account={queryGetAccount.data} />
          <ReadOnlySettingRow
            label="Country"
            value={queryGetAccount.data.taxSettings.homeCountry}
            description="The tax jurisdiction used for this account’s tax calculations."
          />
          <ReadOnlySettingRow
            label="Base currency"
            value={queryGetAccount.data.taxSettings.baseCurrency.name}
            description="The currency used for all valuations and tax calculations."
          />
          <ReadOnlySettingRow
            label="Tax year"
            value={queryGetAccount.data.taxSettings.taxYear}
            description="The tax year period used when generating reports for this account."
          />
        </SettingsStack>
      }
    </SectionContainer>
  );
}

function ParcelMethodSettingRow({ account }: { account: Account }) {
  const disclosure = useDisclosure();

  const canChangeTaxSettings = hasAccountPermission(
    account,
    AccountPermission.ChangeTaxSettings,
  );

  const {
    data: userFinancialYearsData,
    isLoading: isLoadingFinancialYearsData,
  } = useGetAccountFinancialYearsData();

  const defaultParcelMethod =
    account?.taxSettings.defaultParcelMethod ?? DEFAULT_PARCEL_METHOD;

  const allUsersParcelMethods: ParcelMethod[] = useMemo(
    () =>
      (userFinancialYearsData ?? []).map((financialYear): ParcelMethod => {
        const parcelMethod = account?.taxSettings.parcelMethods.find(
          (method) =>
            method.startYear ===
            financialYear.startDate.getUTCFullYear().toString(),
        );

        return {
          startYear: financialYear.startDate.getUTCFullYear().toString(),
          endYear: financialYear.endDate.getUTCFullYear().toString(),
          method: parcelMethod?.method ?? defaultParcelMethod,
        };
      }),
    [
      defaultParcelMethod,
      account?.taxSettings.parcelMethods,
      userFinancialYearsData,
    ],
  );

  const parcelPermission = useMemo(
    () =>
      permissionFor({
        feature: FeatureAccess.FeatureTaxLtfo,
        subscriptions: account?.subscriptions ?? [],
        transactionCount: 0,
        account,
      }).hasPermission,
    [account],
  );

  const canEditParcelMethod = canChangeTaxSettings && parcelPermission;

  return (
    <>
      <SettingRowBase
        isLoading={isLoadingFinancialYearsData}
        label="Parcel matching method"
        description="Apply different tax strategies for matching disposals to acquisitions.
        Use our Lowest-Tax First-Out (LTFO) algorithm for the best tax outcome."
      >
        <Tooltip
          shouldWrapChildren
          label={
            !canEditParcelMethod ?
              "Upgrade to a Tax Saving or Private Wealth plan to change this setting."
            : undefined
          }
        >
          <Button
            variant="outline"
            backgroundColor="white"
            size="sm"
            leftIcon={<FiEdit2 />}
            isDisabled={!canEditParcelMethod}
            onClick={disclosure.onOpen}
          >
            Edit
          </Button>
        </Tooltip>
      </SettingRowBase>
      {disclosure.isOpen && (
        <EditParcelMethodModal
          currentParcelMethods={allUsersParcelMethods}
          disclosure={disclosure}
        />
      )}
    </>
  );
}
